// maths.portll.net — controlled dataset entry.
//
// One component, dispatched on `kind` (the five CLOSED dataset shapes). Warm and
// kid-friendly for the beta shapes (tap-to-tally, tap counters, a big keypad) and a
// plain table for the post-beta shapes so nothing crashes if a Year 7–10 task is
// opened early. Fully CONTROLLED: `value` is the emptyDataset()-shaped payload and
// every change flows back through `onChange` — we never keep the data locally.
//
// Validation is LIVE but SOFT: we surface gentle nudges from validateDataset() and a
// "collect a few more" hint below MIN_OBSERVATIONS, and we NEVER disable an input or
// hard-block. Firestore stores `data` as a map; arrays hold scalars or {maps} only.

import { useEffect, useRef, useState } from "react";
import {
  validateDataset,
  emptyDataset,
  hasEnoughData,
  MIN_OBSERVATIONS,
} from "../lib/datasetShapes.js";
import { injectStyles, Button, Chip, Field, Note } from "./ui.jsx";

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const clampInt = (n, lo, hi) => Math.max(lo, Math.min(hi, Math.trunc(n)));

// Does a payload structurally belong to this kind? Guards against a stale value being
// passed when the task (and therefore the kind) changes under the component.
function matchesKind(kind, data) {
  if (!isObj(data)) return false;
  switch (kind) {
    case "categorical_tally":
      return Array.isArray(data.categories);
    case "trial_outcomes":
      return Array.isArray(data.outcomes);
    case "numerical_list":
      return Array.isArray(data.values);
    case "two_sample":
      return isObj(data.groupA) && isObj(data.groupB);
    case "bivariate_pairs":
      return Array.isArray(data.pairs);
    default:
      return false;
  }
}

const NOUN = {
  categorical_tally: "counted",
  trial_outcomes: "trials",
  numerical_list: "values",
  two_sample: "per group",
  bivariate_pairs: "pairs",
};

// Turn the schema's terse validator errors into warm, non-official language.
function friendlyWarning(kind, err) {
  const e = String(err);
  if (/at least 2 categories/.test(e)) return "Add another thing to count (at least 2).";
  if (/at most 8 categories/.test(e)) return "That’s plenty — keep it to 8 or fewer.";
  if (/each category needs a label/.test(e)) return "Give every group a name.";
  if (/each outcome needs a label/.test(e)) return "Give every outcome a name.";
  if (/at least 2 outcomes/.test(e)) return "Add another possible outcome.";
  if (/need at least 1 value/.test(e)) return "Pop in a value to get started.";
  if (/at most 50 values/.test(e)) return "That’s a big list — 50 is the max.";
  if (/unitLabel is required/.test(e)) return "Add what you’re measuring in (the unit).";
  if (/count for .* must be/.test(e)) return "A count looks off — keep counts whole and not negative.";
  if (/xLabel and yLabel are required/.test(e)) return "Name both things you’re comparing.";
  return e;
}

export default function DatasetEntry({ kind, spec = {}, value, onChange }) {
  const emit = (next) => onChange && onChange(next);
  const data = matchesKind(kind, value) ? value : emptyDataset(kind, spec);

  // Seed the parent once when the incoming value is missing or the wrong shape for
  // this kind. Keyed on kind so a task switch reseeds cleanly.
  const seededFor = useRef(null);
  useEffect(() => {
    if (!matchesKind(kind, value) && seededFor.current !== kind) {
      seededFor.current = kind;
      emit(emptyDataset(kind, spec));
    } else if (matchesKind(kind, value)) {
      seededFor.current = kind;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, value]);

  const update = (patch) => emit({ ...data, ...patch });

  const { errors, observations } = validateDataset(kind, data);
  const enough = hasEnoughData(kind, data);
  const need = MIN_OBSERVATIONS[kind] ?? 1;
  const warnings = (errors || []).map((e) => friendlyWarning(kind, e));

  let body;
  switch (kind) {
    case "categorical_tally":
      body = <CategoricalTally spec={spec} data={data} update={update} />;
      break;
    case "trial_outcomes":
      body = <TrialOutcomes spec={spec} data={data} update={update} />;
      break;
    case "numerical_list":
      body = <NumericalList spec={spec} data={data} update={update} />;
      break;
    case "two_sample":
      body = <TwoSample spec={spec} data={data} update={update} />;
      break;
    case "bivariate_pairs":
      body = <BivariatePairs spec={spec} data={data} update={update} />;
      break;
    default:
      body = <Note tone="danger">This activity uses an unknown data kind ({String(kind)}).</Note>;
  }

  return (
    <div className="de-root">
      {body}

      <div className="de-footer">
        <div className="de-meter" aria-live="polite">
          <Chip tone={enough ? "ok" : "neutral"}>
            {observations} {NOUN[kind] || "collected"}
          </Chip>
          {!enough && (
            <span className="de-meter__hint">
              A few more and the questions get better — aim for about {need}.
            </span>
          )}
        </div>
        {warnings.length > 0 && (
          <ul className="de-warnings">
            {warnings.slice(0, 4).map((w, i) => (
              <li key={i}>
                <Note tone="warn">{w}</Note>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── categorical_tally ─────────────────────────────────────────────────────────
   { categories: [{label, count}], unitLabel } */
function CategoricalTally({ spec, data, update }) {
  const categories = Array.isArray(data.categories) ? data.categories : [];
  const unit = data.unitLabel || spec.unitLabel || "";
  const kidDefined =
    spec.categories === "kid_defined" ||
    !Array.isArray(spec.suggestedCategories) ||
    spec.suggestedCategories.length === 0;

  const setCount = (i, delta) => {
    const next = categories.map((c, j) =>
      j === i ? { ...c, count: clampInt((Number(c.count) || 0) + delta, 0, 999) } : c
    );
    update({ categories: next });
  };
  const setLabel = (i, label) =>
    update({ categories: categories.map((c, j) => (j === i ? { ...c, label } : c)) });
  const removeCat = (i) => update({ categories: categories.filter((_, j) => j !== i) });
  const addCat = (label) => {
    if (categories.length >= 8) return;
    update({ categories: [...categories, { label, count: 0 }] });
  };

  return (
    <div className="de-block">
      <div className="de-grid">
        {categories.map((c, i) => (
          <div className="de-cat" key={i}>
            <TallyTile
              label={c.label || (kidDefined ? `Group ${i + 1}` : "")}
              count={Number(c.count) || 0}
              unit={unit}
              onInc={() => setCount(i, +1)}
              onDec={() => setCount(i, -1)}
            />
            <div className="de-cat__ctrls">
              <button
                type="button"
                className="de-step"
                onClick={() => setCount(i, -1)}
                aria-label={`One less ${c.label || `group ${i + 1}`}`}
              >
                −1
              </button>
              {kidDefined && (
                <input
                  className="de-label-input"
                  value={c.label}
                  placeholder={`Name #${i + 1}`}
                  onChange={(e) => setLabel(i, e.target.value)}
                  aria-label={`Name for group ${i + 1}`}
                />
              )}
              {kidDefined && categories.length > 2 && (
                <button
                  type="button"
                  className="de-remove"
                  onClick={() => removeCat(i)}
                  aria-label={`Remove ${c.label || `group ${i + 1}`}`}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {kidDefined && categories.length < 8 && <AddCategory onAdd={addCat} />}
      <p className="de-hint">Tap a card to add one{unit ? ` ${unit}` : ""}. Hold it (or use −1) to take one away.</p>
    </div>
  );
}

// Big tap-to-tally card. Quick tap → +1. Long-press (≈550ms) → −1 (bonus to the
// always-present −1 button, so the affordance is never touch-only for a11y).
function TallyTile({ label, count, unit, onInc, onDec }) {
  const timer = useRef(null);
  const didLong = useRef(false);
  const start = () => {
    didLong.current = false;
    timer.current = setTimeout(() => {
      didLong.current = true;
      onDec();
    }, 550);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const click = () => {
    if (didLong.current) {
      didLong.current = false;
      return;
    }
    onInc();
  };
  return (
    <button
      type="button"
      className="de-tile"
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onClick={click}
    >
      <span className="de-tile__count">{count}</span>
      <span className="de-tile__label">
        {label}
        {unit ? <span className="de-tile__unit"> {unit}</span> : null}
      </span>
    </button>
  );
}

function AddCategory({ onAdd }) {
  const [label, setLabel] = useState("");
  const submit = () => {
    const t = label.trim();
    if (!t) return;
    onAdd(t);
    setLabel("");
  };
  return (
    <div className="de-addrow">
      <input
        className="de-input"
        value={label}
        placeholder="Add something to count…"
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        aria-label="New category name"
      />
      <Button variant="secondary" size="sm" onClick={submit}>
        Add
      </Button>
    </div>
  );
}

/* ── trial_outcomes ────────────────────────────────────────────────────────────
   { trials, outcomes: [{label, count}], expectedModel } */
function TrialOutcomes({ data, update }) {
  const outcomes = Array.isArray(data.outcomes) ? data.outcomes : [];
  const total = outcomes.reduce((s, o) => s + (Number(o.count) || 0), 0);

  const commit = (next) =>
    update({ outcomes: next, trials: next.reduce((s, o) => s + (Number(o.count) || 0), 0) });
  const setCount = (i, delta) =>
    commit(
      outcomes.map((o, j) =>
        j === i ? { ...o, count: clampInt((Number(o.count) || 0) + delta, 0, 9999) } : o
      )
    );
  const setLabel = (i, label) =>
    update({ outcomes: outcomes.map((o, j) => (j === i ? { ...o, label } : o)) });

  return (
    <div className="de-block">
      <div className="de-total">
        <span className="de-total__num">{total}</span>
        <span className="de-total__label">trials so far</span>
      </div>
      <div className="de-grid">
        {outcomes.map((o, i) => (
          <div className="de-cat" key={i}>
            <TallyTile
              label={o.label || `Outcome ${i + 1}`}
              count={Number(o.count) || 0}
              unit=""
              onInc={() => setCount(i, +1)}
              onDec={() => setCount(i, -1)}
            />
            <div className="de-cat__ctrls">
              <button
                type="button"
                className="de-step"
                onClick={() => setCount(i, -1)}
                aria-label={`One less ${o.label || `outcome ${i + 1}`}`}
              >
                −1
              </button>
              {!o.label && (
                <input
                  className="de-label-input"
                  value={o.label}
                  placeholder={`Outcome #${i + 1}`}
                  onChange={(e) => setLabel(i, e.target.value)}
                  aria-label={`Name for outcome ${i + 1}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="de-hint">Tap the outcome each round. The trials total adds up on its own.</p>
    </div>
  );
}

/* ── numerical_list ────────────────────────────────────────────────────────────
   { values: number[], unitLabel } — large keypad + removable chip strip */
function NumericalList({ spec, data, update }) {
  const values = Array.isArray(data.values) ? data.values : [];
  const unit = data.unitLabel || spec.unitLabel || "";

  const add = (n) => {
    if (values.length >= 50) return;
    update({ values: [...values, n] });
  };
  const removeAt = (i) => update({ values: values.filter((_, j) => j !== i) });

  return (
    <div className="de-block">
      <Keypad unit={unit} onAdd={add} />
      {values.length > 0 ? (
        <div className="de-chips" aria-label="Values you've added">
          {values.map((v, i) => (
            <button
              type="button"
              key={i}
              className="de-chip"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${v}${unit ? ` ${unit}` : ""}`}
            >
              {fmt(v)}
              {unit ? <span className="de-chip__unit"> {unit}</span> : null}
              <span className="de-chip__x" aria-hidden="true">
                ×
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="de-hint">Add each measurement with the keypad. Tap a value to remove it.</p>
      )}
    </div>
  );
}

// Big numeric keypad. Supports one decimal point and a ± sign toggle.
function Keypad({ unit, onAdd }) {
  const [buf, setBuf] = useState("");
  const press = (ch) => {
    setBuf((b) => {
      if (ch === "⌫") return b.slice(0, -1);
      if (ch === "±") return b.startsWith("-") ? b.slice(1) : "-" + b;
      if (ch === ".") return b.includes(".") ? b : (b === "" || b === "-" ? b + "0." : b + ".");
      return b + ch;
    });
  };
  const commit = () => {
    const n = parseFloat(buf);
    if (Number.isFinite(n)) onAdd(n);
    setBuf("");
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "±", "0", "."];
  return (
    <div className="de-keypad">
      <div className="de-display" aria-live="polite">
        <span className="de-display__val">{buf || "0"}</span>
        {unit ? <span className="de-display__unit">{unit}</span> : null}
      </div>
      <div className="de-keys">
        {keys.map((k) => (
          <button type="button" key={k} className="de-key" onClick={() => press(k)}>
            {k}
          </button>
        ))}
        <button type="button" className="de-key de-key--util" onClick={() => press("⌫")} aria-label="Delete">
          ⌫
        </button>
        <button type="button" className="de-key de-key--add" onClick={commit}>
          Add value
        </button>
      </div>
    </div>
  );
}

/* ── two_sample (POST-BETA, must render) ───────────────────────────────────────
   { groupA:{label, values}, groupB:{label, values} } */
function TwoSample({ data, update }) {
  const groups = ["groupA", "groupB"];
  const setLabel = (key, label) => update({ [key]: { ...data[key], label } });
  const addVal = (key, n) => {
    const g = data[key] || { label: "", values: [] };
    update({ [key]: { ...g, values: [...(g.values || []), n] } });
  };
  const removeVal = (key, i) => {
    const g = data[key] || { label: "", values: [] };
    update({ [key]: { ...g, values: (g.values || []).filter((_, j) => j !== i) } });
  };
  return (
    <div className="de-block">
      <Note tone="neutral">Comparing two groups — this activity type is coming next term.</Note>
      <div className="de-twocol">
        {groups.map((key) => {
          const g = data[key] || { label: "", values: [] };
          return (
            <div className="de-samplecol" key={key}>
              <Field label={key === "groupA" ? "First group" : "Second group"}>
                <input
                  className="de-input"
                  value={g.label || ""}
                  placeholder="Name this group"
                  onChange={(e) => setLabel(key, e.target.value)}
                  aria-label={`${key} name`}
                />
              </Field>
              <InlineNumberAdder onAdd={(n) => addVal(key, n)} />
              <NumChips
                values={g.values || []}
                onRemove={(i) => removeVal(key, i)}
                label={`${g.label || key} values`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── bivariate_pairs (POST-BETA, must render) ──────────────────────────────────
   { pairs:[{x,y}], xLabel, yLabel } — NEVER [x,y] nested arrays */
function BivariatePairs({ data, update }) {
  const pairs = Array.isArray(data.pairs) ? data.pairs : [];
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const addPair = () => {
    const nx = parseFloat(x);
    const ny = parseFloat(y);
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
    if (pairs.length >= 40) return;
    update({ pairs: [...pairs, { x: nx, y: ny }] });
    setX("");
    setY("");
  };
  const removePair = (i) => update({ pairs: pairs.filter((_, j) => j !== i) });

  return (
    <div className="de-block">
      <Note tone="neutral">Plotting pairs of numbers — this activity type is coming next term.</Note>
      <div className="de-twocol">
        <Field label="First measurement (x)">
          <input
            className="de-input"
            value={data.xLabel || ""}
            placeholder="e.g. Height"
            onChange={(e) => update({ xLabel: e.target.value })}
          />
        </Field>
        <Field label="Second measurement (y)">
          <input
            className="de-input"
            value={data.yLabel || ""}
            placeholder="e.g. Arm span"
            onChange={(e) => update({ yLabel: e.target.value })}
          />
        </Field>
      </div>
      <div className="de-pairrow">
        <input
          className="de-input"
          inputMode="decimal"
          value={x}
          placeholder={data.xLabel || "x"}
          onChange={(e) => setX(e.target.value)}
          aria-label="x value"
        />
        <span className="de-pairrow__sep">,</span>
        <input
          className="de-input"
          inputMode="decimal"
          value={y}
          placeholder={data.yLabel || "y"}
          onChange={(e) => setY(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addPair()}
          aria-label="y value"
        />
        <Button variant="secondary" size="sm" onClick={addPair}>
          Add pair
        </Button>
      </div>
      {pairs.length > 0 && (
        <table className="de-table">
          <thead>
            <tr>
              <th>{data.xLabel || "x"}</th>
              <th>{data.yLabel || "y"}</th>
              <th aria-label="remove" />
            </tr>
          </thead>
          <tbody>
            {pairs.map((p, i) => (
              <tr key={i}>
                <td>{fmt(p.x)}</td>
                <td>{fmt(p.y)}</td>
                <td>
                  <button
                    type="button"
                    className="de-remove"
                    onClick={() => removePair(i)}
                    aria-label={`Remove pair ${i + 1}`}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Shared compact numeric adder (post-beta table shapes).
function InlineNumberAdder({ onAdd }) {
  const [v, setV] = useState("");
  const submit = () => {
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      onAdd(n);
      setV("");
    }
  };
  return (
    <div className="de-addrow">
      <input
        className="de-input"
        inputMode="decimal"
        value={v}
        placeholder="Add a number"
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        aria-label="New value"
      />
      <Button variant="secondary" size="sm" onClick={submit}>
        Add
      </Button>
    </div>
  );
}

function NumChips({ values, onRemove, label }) {
  if (!values.length) return null;
  return (
    <div className="de-chips" aria-label={label}>
      {values.map((v, i) => (
        <button
          type="button"
          key={i}
          className="de-chip"
          onClick={() => onRemove(i)}
          aria-label={`Remove ${v}`}
        >
          {fmt(v)}
          <span className="de-chip__x" aria-hidden="true">
            ×
          </span>
        </button>
      ))}
    </div>
  );
}

// Trim float noise for display without changing stored values.
function fmt(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return String(v);
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 1000) / 1000);
}

const DE_CSS = `
.de-root{display:flex;flex-direction:column;gap:1rem;}
.de-block{display:flex;flex-direction:column;gap:1rem;}
.de-hint{margin:.2rem 0 0;color:var(--ink-soft);font-size:.9rem;}

.de-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:.75rem;}
.de-cat{display:flex;flex-direction:column;gap:.5rem;}
.de-tile{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.15rem;
  min-height:104px;padding:.75rem;background:var(--card);color:var(--ink);
  border:2px solid color-mix(in srgb,var(--brand) 26%,#fff);border-radius:var(--radius);
  box-shadow:var(--shadow);cursor:pointer;user-select:none;-webkit-user-select:none;
  -webkit-touch-callout:none;touch-action:manipulation;transition:transform .05s ease;}
.de-tile:active{transform:scale(.97);background:color-mix(in srgb,var(--brand) 8%,#fff);}
.de-tile__count{font-size:2.4rem;font-weight:800;line-height:1;color:var(--brand-ink);}
.de-tile__label{font-weight:600;text-align:center;color:var(--ink);}
.de-tile__unit{color:var(--ink-soft);font-weight:500;}
.de-cat__ctrls{display:flex;align-items:center;gap:.4rem;}
.de-step{min-width:44px;min-height:44px;padding:0 .6rem;border-radius:10px;background:var(--card);
  color:var(--brand-ink);border:1px solid var(--line);font-weight:700;}
.de-step:active{transform:translateY(1px);}
.de-label-input{flex:1 1 auto;min-width:0;font:inherit;min-height:44px;padding:.4rem .6rem;
  border:1px solid var(--line);border-radius:10px;background:var(--card);color:var(--ink);}
.de-remove{min-width:44px;min-height:44px;border-radius:10px;background:var(--card);
  color:var(--danger);border:1px solid color-mix(in srgb,var(--danger) 30%,#fff);font-size:1.2rem;line-height:1;}

.de-addrow{display:flex;gap:.5rem;align-items:stretch;}
.de-input{flex:1 1 auto;min-width:0;font:inherit;min-height:48px;padding:.6rem .75rem;
  border:1px solid var(--line);border-radius:10px;background:var(--card);color:var(--ink);}
.de-input:focus-visible{outline:2px solid var(--sky);outline-offset:1px;}

.de-total{display:flex;align-items:baseline;gap:.5rem;padding:.6rem .9rem;background:color-mix(in srgb,var(--brand) 6%,#fff);
  border:1px solid color-mix(in srgb,var(--brand) 20%,#fff);border-radius:var(--radius);}
.de-total__num{font-size:2rem;font-weight:800;color:var(--brand-ink);line-height:1;}
.de-total__label{color:var(--ink-soft);font-weight:600;}

.de-keypad{display:flex;flex-direction:column;gap:.6rem;max-width:340px;}
.de-display{display:flex;align-items:baseline;justify-content:flex-end;gap:.4rem;min-height:56px;
  padding:.5rem .9rem;background:var(--card);border:2px solid var(--line);border-radius:12px;}
.de-display__val{font-size:1.8rem;font-weight:800;color:var(--brand-ink);}
.de-display__unit{color:var(--ink-soft);font-weight:600;}
.de-keys{display:grid;grid-template-columns:repeat(3,1fr);gap:.5rem;}
.de-key{min-height:56px;font-size:1.3rem;font-weight:700;background:var(--card);color:var(--ink);
  border:1px solid var(--line);border-radius:12px;}
.de-key:active{transform:translateY(1px);background:color-mix(in srgb,var(--brand) 8%,#fff);}
.de-key--util{color:var(--ink-soft);}
.de-key--add{grid-column:1 / -1;background:var(--brand);color:#fff;border-color:var(--brand);font-size:1.05rem;}

.de-chips{display:flex;flex-wrap:wrap;gap:.5rem;}
.de-chip{display:inline-flex;align-items:center;gap:.3rem;min-height:44px;padding:.35rem .7rem;
  background:color-mix(in srgb,var(--sky) 8%,#fff);color:#1e4d63;
  border:1px solid color-mix(in srgb,var(--sky) 26%,#fff);border-radius:999px;font-weight:700;}
.de-chip:active{transform:translateY(1px);}
.de-chip__unit{font-weight:500;color:var(--ink-soft);}
.de-chip__x{opacity:.6;font-weight:700;}

.de-twocol{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:1rem;}
.de-samplecol{display:flex;flex-direction:column;gap:.6rem;padding:.9rem;background:var(--card);
  border:1px solid var(--line);border-radius:var(--radius);}
.de-pairrow{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;}
.de-pairrow__sep{font-weight:800;color:var(--ink-soft);}
.de-table{width:100%;border-collapse:collapse;}
.de-table th,.de-table td{text-align:left;padding:.45rem .6rem;border-bottom:1px solid var(--line);}
.de-table th{color:var(--ink-soft);font-size:.85rem;}

.de-footer{display:flex;flex-direction:column;gap:.5rem;border-top:1px solid var(--line);padding-top:.9rem;}
.de-meter{display:flex;align-items:center;gap:.6rem;flex-wrap:wrap;}
.de-meter__hint{color:var(--ink-soft);font-size:.9rem;}
.de-warnings{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.25rem;}
`;

injectStyles("maths-datasetentry-styles", DE_CSS);
